import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { RiSettings3Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { feedsApi } from "@/modules/cf-feed/presentation/api/client";
import {
  CATALOG_PAGE_LIMIT,
  feedsKeys,
  useAddSessionProduct,
  useAddSessionProductsBySelection,
  useCatalogInfiniteQuery,
  useConfirmPost,
  useFeedSessionProductsInfiniteQuery,
  useFeedsQuery,
  usePatchSessionProduct,
  usePatchSessionWarehouse,
  usePostProductsContext,
  usePosWarehousesQuery,
  useRemoveSessionProduct,
  useRemoveSessionProductsBulk,
  useSyncFeeds,
} from "@/modules/cf-feed/presentation/api/queries";
import type {
  AdminCatalogItem,
  FeedListItem,
} from "@/modules/cf-feed/presentation/api/types";
import { buildCatalogAddInput } from "@/modules/cf-feed/presentation/lib/catalog-add";
import {
  isPreAddDraftActive,
  type PreAddSessionOptionsDraft,
  preAddDraftSelectedCount,
  toPreAddApiPayload,
} from "@/modules/cf-feed/presentation/lib/pre-add-session-options";
import {
  type ProductAllocationDraft,
  parseProductAllocation,
  productAllocationError,
} from "@/modules/cf-feed/presentation/lib/product-allocation";
import { CatalogTable } from "@/modules/cf-feed/presentation/ui/CatalogTable";
import {
  FeedProductsTable,
  sessionProductOptionSignature,
} from "@/modules/cf-feed/presentation/ui/FeedProductsTable";
import { PreAddSessionOptionsDialog } from "@/modules/cf-feed/presentation/ui/PreAddSessionOptionsDialog";
import { ProductAllocationFields } from "@/modules/cf-feed/presentation/ui/ProductAllocationFields";
import { SessionProductOptionsDialog } from "@/modules/cf-feed/presentation/ui/SessionProductOptionsDialog";
import { useWorkspaceEnabled } from "@/modules/cf-live/presentation/api/workspace-client";
import { LiveWorkspacePage } from "@/modules/cf-live/presentation/pages/LiveWorkspacePage";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { FacebookPageCombobox } from "@/modules/facebook/presentation/ui/FacebookPageCombobox";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import { AppImage } from "@/shared/ui/AppImage";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { QueryState } from "@/shared/ui/QueryState";

type PostProductsSearch = {
  pageId?: string;
  feedId?: string;
};

function getFeedLabel(feed: FeedListItem) {
  return feed.messagePreview ?? feed.title ?? feed.facebookFeedId;
}

function ImageComboboxRow({
  image,
  title,
  detail,
  compact = false,
}: {
  image: string | null;
  title: string;
  detail?: string;
  compact?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <AppImage
        src={image}
        alt=""
        className={`${compact ? "size-6" : "size-8"} shrink-0 rounded border bg-muted`}
        showLoading={false}
      />
      <span className="min-w-0 flex-1 truncate">
        <span className="block truncate text-sm" title={title}>
          {title}
        </span>
        {!compact && detail ? (
          <span className="block truncate text-muted-foreground text-xs">
            {detail}
          </span>
        ) : null}
      </span>
    </span>
  );
}

export function PostProductsPage() {
  const search = useSearch({ from: "/app/feeds/post-products" });
  const capability = useWorkspaceEnabled();
  if (capability.isLoading) return <p className="p-6">ກຳລັງໂຫຼດ…</p>;
  if (search.feedId && capability.data?.enabled)
    return <LiveWorkspacePage kind="feed" id={search.feedId} />;
  return <LegacyPostProductsPage />;
}

function LegacyPostProductsPage() {
  const search = useSearch({
    from: "/app/feeds/post-products",
  }) as PostProductsSearch;
  const nav = useNavigate({ from: "/app/feeds/post-products" });
  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  const [catalogQ, setCatalogQ] = useState("");
  const [selectedCatalogItems, setSelectedCatalogItems] = useState<
    Map<string, AdminCatalogItem>
  >(() => new Map());
  const [selectedSessionProductIds, setSelectedSessionProductIds] = useState<
    Set<string>
  >(() => new Set());
  const [optionsTarget, setOptionsTarget] = useState<{
    ids: string[];
    title: string;
    discountPercent: number | null;
    discountFixedLak: number | null;
  } | null>(null);
  const [preAddDraft, setPreAddDraft] =
    useState<PreAddSessionOptionsDraft | null>(null);
  const [preAddDialogOpen, setPreAddDialogOpen] = useState(false);
  const [allocationDraft, setAllocationDraft] =
    useState<ProductAllocationDraft>({
      allocationMode: "limit",
      allocatedQuantity: undefined,
    });
  const addingRef = useRef(false);
  const allocationError = productAllocationError(allocationDraft);
  const queryClient = useQueryClient();
  const debouncedCatalogSearch = useDebounceCallback((value: string) => {
    setCatalogQ(value.trim());
  }, 350);

  const pagesQuery = useFacebookPagesQuery();
  const activePages =
    pagesQuery.data?.pages.filter((p) => p.status !== "disconnected") ?? [];

  const pageId = search.pageId;
  const feedId = search.feedId;
  const allocationScope = useRef(feedId);
  useEffect(() => {
    if (allocationScope.current === feedId) return;
    allocationScope.current = feedId;
    setAllocationDraft({
      allocationMode: "limit",
      allocatedQuantity: undefined,
    });
  }, [feedId]);

  const feedsQuery = useFeedsQuery({
    pageId,
    // dropdown เลือกโพสต์ — โหลดชุดใหญ่พอในรอบเดียว (ไม่ใช่ตาราง paginate)
    limit: 100,
    offset: 0,
  });
  const contextQuery = usePostProductsContext(feedId);
  const sessionProductsQuery = useFeedSessionProductsInfiniteQuery(
    feedId ?? "",
    Boolean(feedId),
  );
  const sessionWarehouseId =
    contextQuery.data?.session.retailStockWarehouseId ?? undefined;
  const catalogQuery = useCatalogInfiniteQuery({
    q: catalogQ || undefined,
    pageId,
    warehouseId: sessionWarehouseId,
    limit: CATALOG_PAGE_LIMIT,
    groupByProduct: true,
    enabled: Boolean(pageId && feedId && sessionWarehouseId),
  });
  const warehousesQuery = usePosWarehousesQuery();
  const patchSessionWarehouse = usePatchSessionWarehouse(feedId ?? "");
  const syncFeeds = useSyncFeeds();
  const addProduct = useAddSessionProduct();
  const addProducts = useAddSessionProductsBySelection();
  const patchSessionProduct = usePatchSessionProduct(feedId ?? "");
  const removeProduct = useRemoveSessionProduct(feedId ?? "");
  const removeProductsBulk = useRemoveSessionProductsBulk(feedId ?? "");
  const confirmPost = useConfirmPost();

  const feeds = feedsQuery.data?.feeds ?? [];
  const catalogItems = useMemo(
    () => catalogQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [catalogQuery.data?.pages],
  );
  const catalogProducts = useMemo(
    () => catalogQuery.data?.pages.flatMap((page) => page.products ?? []) ?? [],
    [catalogQuery.data?.pages],
  );
  const catalogTotal = catalogQuery.data?.pages[0]?.total ?? 0;
  const sessionProductGroups = useMemo(
    () =>
      sessionProductsQuery.data?.pages.flatMap((page) => page.products ?? []) ??
      [],
    [sessionProductsQuery.data?.pages],
  );
  const sessionProducts = useMemo(
    () => sessionProductGroups.flatMap((product) => product.variants),
    [sessionProductGroups],
  );
  const selectedCatalogIds = useMemo(
    () => new Set(selectedCatalogItems.keys()),
    [selectedCatalogItems],
  );
  const sessionProductVariantIds = useMemo(
    () =>
      new Set([
        ...(contextQuery.data?.sessionProductVariantIds ?? []),
        ...(contextQuery.data?.sessionProducts ?? []).map(
          (product) => product.variantId,
        ),
        ...sessionProducts.map((product) => product.variantId),
      ]),
    [contextQuery.data, sessionProducts],
  );
  const sessionProductTotal =
    contextQuery.data?.sessionProductVariantIds?.length ??
    sessionProductsQuery.data?.pages[0]?.total ??
    0;
  const selectedAddableItems = useMemo(
    () =>
      [...selectedCatalogItems.values()].filter(
        (item) =>
          (item.isPreorder || item.availableToAdd > 0) &&
          !sessionProductVariantIds.has(item.variantId),
      ),
    [selectedCatalogItems, sessionProductVariantIds],
  );

  const handleCatalogSelectionChange = (nextIds: Set<string>) => {
    setSelectedCatalogItems((prev) => {
      const next = new Map(prev);
      for (const item of catalogItems) {
        if (nextIds.has(item.variantId)) next.set(item.variantId, item);
        else next.delete(item.variantId);
      }
      return next;
    });
  };

  const removeSelectedCatalogItems = (variantIds: string[]) => {
    if (variantIds.length === 0) return;
    setSelectedCatalogItems((prev) => {
      const next = new Map(prev);
      for (const id of variantIds) next.delete(id);
      return next;
    });
  };

  // ล้าง selection ของ session product ที่หาย (เช่น ถูกลบ) เพื่อกัน state ค้าง
  useEffect(() => {
    const ids = new Set(sessionProducts.map((p) => p.id));
    setSelectedSessionProductIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (ids.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [sessionProducts]);

  useEffect(() => {
    setSelectedCatalogItems((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, item] of prev) {
        if (
          sessionProductVariantIds.has(id) ||
          (!item.isPreorder && item.availableToAdd <= 0)
        ) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sessionProductVariantIds]);

  useEffect(() => {
    setSelectedCatalogItems((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, item] of prev) {
        const current = catalogItems.find(
          (candidate) => candidate.variantId === id,
        );
        if (current && current !== item) {
          next.set(id, current);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [catalogItems]);

  const setPage = (id: string) => {
    setSelectedCatalogItems(new Map());
    nav({
      search: { pageId: id, feedId: undefined },
    });
  };

  const setFeed = (id: string) => {
    if (!pageId) return;
    nav({
      search: { pageId, feedId: id },
    });
  };

  const handleAdd = async (item: AdminCatalogItem) => {
    if (addingRef.current) return;
    if (!feedId) {
      toast.error("ກະລຸນາເລືອກໂພສກ່ອນ");
      return;
    }
    if (allocationError) {
      toast.error(formatCfApiErrorMessage(allocationError));
      return;
    }
    addingRef.current = true;
    try {
      await addProduct.mutateAsync({
        feedId,
        ...buildCatalogAddInput(item),
        ...parseProductAllocation(allocationDraft),
        ...toPreAddApiPayload(preAddDraft),
      });
      removeSelectedCatalogItems([item.variantId]);
      if (preAddDraft && !preAddDraft.keepForNextAdds) {
        setPreAddDraft(null);
      }
    } catch {
      return;
    } finally {
      addingRef.current = false;
    }
  };

  const handleCatalogSearchKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") return;
    const code = event.currentTarget.value.trim();
    if (!code || !feedId) return;
    event.preventDefault();
    try {
      const result = await feedsApi.catalog({
        pageId,
        warehouseId: sessionWarehouseId,
        q: code,
        limit: 20,
        offset: 0,
      });
      const matches = result.items.filter(
        (item) =>
          item.sku.toLowerCase() === code.toLowerCase() ||
          item.barcodePrefix?.toLowerCase() === code.toLowerCase(),
      );
      const onlyMatch = matches.length === 1 ? matches[0] : undefined;
      if (onlyMatch) {
        await handleAdd(onlyMatch);
        setCatalogSearchInput("");
        setCatalogQ("");
        return;
      }
      setCatalogSearchInput(code);
      setCatalogQ(code);
      toast.info(
        matches.length > 1
          ? `ພົບ ${matches.length} variant — ເລືອກຈາກລາຍການ`
          : "ບໍ່ພົບ barcode ທີ່ກົງກັນ",
      );
    } catch {
      toast.error("ຄົ້ນຫາ barcode ລົ້ມເຫຼວ");
    }
  };

  const handleBulkAdd = async () => {
    if (addingRef.current || allocationError) return;
    if (!feedId) {
      toast.error("ກະລຸນາເລືອກໂພສກ່ອນ");
      return;
    }
    if (selectedAddableItems.length === 0) {
      toast.error("ກະລຸນາເລືອກສິນຄ້າກ່ອນ");
      return;
    }

    const variantIds = selectedAddableItems.map((item) => item.variantId);
    addingRef.current = true;
    try {
      const result = await addProducts.mutateAsync({
        feedId,
        selection: { mode: "selected", variantIds },
        ...parseProductAllocation(allocationDraft),
        ...toPreAddApiPayload(preAddDraft),
      });
      const failed = new Set(result.failed.map((failure) => failure.variantId));
      removeSelectedCatalogItems(variantIds.filter((id) => !failed.has(id)));
      if (preAddDraft && !preAddDraft.keepForNextAdds) {
        setPreAddDraft(null);
      }
    } catch {
      // mutation hook แสดงข้อความผิดพลาดแล้ว
    } finally {
      addingRef.current = false;
    }
  };

  const handleAddAllMatching = async () => {
    if (addingRef.current || allocationError) return;
    if (!feedId) {
      toast.error("ກະລຸນາເລືອກໂພສກ່ອນ");
      return;
    }
    addingRef.current = true;
    try {
      await addProducts.mutateAsync({
        feedId,
        ...parseProductAllocation(allocationDraft),
        selection: {
          mode: "all_matching",
          q: catalogQ || undefined,
        },
        ...toPreAddApiPayload(preAddDraft),
      });
      setSelectedCatalogItems(new Map());
      if (preAddDraft && !preAddDraft.keepForNextAdds) {
        setPreAddDraft(null);
      }
    } catch {
      // mutation hook แสดงข้อความผิดพลาดแล้ว
    } finally {
      addingRef.current = false;
    }
  };

  const warehouses = warehousesQuery.data?.items ?? [];
  const canChangeWarehouse =
    contextQuery.data?.session.status === "draft" && Boolean(feedId);

  const handleBulkRemove = async (ids: string[]) => {
    if (ids.length === 0) {
      toast.error("ກະລຸນາເລືອກສິນຄ້າກ່ອນ");
      return;
    }
    try {
      await removeProductsBulk.mutateAsync(ids);
      setSelectedSessionProductIds(new Set());
    } catch {
      return;
    }
  };

  const handleBulkEditOptions = (ids: string[]) => {
    const rows = sessionProducts.filter((p) => ids.includes(p.id));
    if (rows.length < 2) return;
    const signatures = new Set(
      rows.map((r) => sessionProductOptionSignature(r.options ?? [])),
    );
    if (signatures.size > 1) {
      toast.error("ເລືອກສິນຄ້າທີ່ມີ option ຊຸດຄືກັນເທົ່ານັ້ນ");
      return;
    }
    const first = rows[0];
    if (!first) return;
    setOptionsTarget({
      ids,
      title: `${ids.length} ລາຍການ (option ຄືກັນ)`,
      discountPercent: first.discountPercent,
      discountFixedLak: first.discountFixedLak,
    });
  };

  const handleConfirm = async () => {
    if (!feedId) {
      toast.error("ກະລຸນາເລືອກໂພສກ່ອນ");
      return;
    }
    try {
      await confirmPost.mutateAsync(feedId);
    } catch {
      return;
    }
    nav({ to: "/app/feeds" });
  };

  const selectedPage = activePages.find((p) => p.id === pageId);
  const selectedFeed = feeds.find((f) => f.id === feedId);
  const selectedFeedLabel =
    selectedFeed?.messagePreview ??
    selectedFeed?.title ??
    (feedId ? feedId : null);

  return (
    <>
      <Header />

      <Main>
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-muted-foreground text-sm">
          <Link to="/app/feeds" className="hover:text-foreground">
            ຈັດການໂພສ
          </Link>
          <ChevronRight className="size-4" />
          <span className="text-foreground">ສ້າງສິນຄ້າໃນໂພສ</span>
        </nav>

        <div className="mb-6 grid min-w-0 gap-4 rounded-xl border bg-card p-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
          <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-1">
              <span className="text-muted-foreground text-xs">ເພຈ</span>
              <FacebookPageCombobox
                pages={activePages}
                value={pageId ?? ""}
                onValueChange={(id) => {
                  if (id && feeds.length === 0) {
                    syncFeeds.mutate({ pageId: id });
                  }
                  setPage(id);
                }}
                placeholder="ກະລຸນາເລືອກເພຈກ່ອນ"
                disabled={pagesQuery.isLoading}
                queryKey={["cf-post-products-pages"]}
              />
            </div>

            <div className="min-w-0 space-y-1">
              <span className="text-muted-foreground text-xs">ໂພສ</span>
              <InfiniteCombobox<FeedListItem>
                queryKey={[
                  "cf-post-products-feeds",
                  "post-and-live",
                  pageId ?? "",
                ]}
                value={feedId ?? ""}
                onValueChange={setFeed}
                placeholder="ກະລຸນາເລືອກໂພສກ່ອນ"
                disabled={!pageId}
                queryFn={async ({ search, pageParam }) => {
                  if (!pageId) return { items: [], nextPage: null };
                  const offset = (pageParam - 1) * 50;
                  const result = await feedsApi.list({
                    pageId,
                    q: search || undefined,
                    limit: 50,
                    offset,
                  });
                  return {
                    items: result.feeds,
                    nextPage:
                      offset + result.feeds.length < result.total
                        ? pageParam + 1
                        : null,
                  };
                }}
                preloadQueryFn={async (id) => {
                  try {
                    const result = await feedsApi.get(id);
                    return result.feed;
                  } catch {
                    return null;
                  }
                }}
                getLabel={getFeedLabel}
                getValue={(feed) => feed.id}
                renderItem={(feed) => (
                  <ImageComboboxRow
                    image={feed.thumbnailUrl}
                    title={getFeedLabel(feed)}
                    detail={feed.pageName}
                  />
                )}
                renderSelected={(feed) => (
                  <ImageComboboxRow
                    image={feed.thumbnailUrl}
                    title={getFeedLabel(feed)}
                    compact
                  />
                )}
              />
            </div>

            <div className="min-w-0 space-y-1">
              <span className="text-muted-foreground text-xs">ຄັງສິນຄ້າ</span>
              <Select
                value={sessionWarehouseId ?? ""}
                onValueChange={(id) => {
                  if (feedId && id) {
                    patchSessionWarehouse.mutate(id);
                  }
                }}
                disabled={
                  !canChangeWarehouse || patchSessionWarehouse.isPending
                }
              >
                <SelectTrigger className="w-full min-w-0">
                  <SelectValue placeholder="ເລືອກຄັງ" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                      {w.isMainStock ? " (ຫຼັກ)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="button"
            className="w-full sm:w-auto sm:justify-self-end"
            disabled={!feedId || confirmPost.isPending}
            onClick={() => void handleConfirm()}
          >
            ຢັ້ງຢືນໂພສ
          </Button>
        </div>

        {(selectedPage || selectedFeedLabel) && (
          <p className="mb-4 flex min-w-0 flex-col gap-1 text-muted-foreground text-sm sm:flex-row sm:gap-3">
            {selectedPage ? (
              <span className="min-w-0 shrink-0">
                ເພຈ:{" "}
                <strong className="text-foreground">
                  {selectedPage.pageName}
                </strong>
              </span>
            ) : null}
            {selectedFeedLabel ? (
              <span className="flex min-w-0 gap-1">
                ໂພສ:{" "}
                <strong
                  className="min-w-0 truncate text-foreground"
                  title={selectedFeedLabel}
                >
                  {selectedFeedLabel}
                </strong>
              </span>
            ) : null}
          </p>
        )}

        <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
          <section className="min-w-0 overflow-hidden rounded-xl border bg-card">
            <div className="flex flex-col gap-3 border-b p-4">
              <div>
                <h2 className="font-medium">ລາຍການສິນຄ້າ ({catalogTotal})</h2>
                <p className="text-muted-foreground text-xs">
                  ເລືອກແລ້ວ {selectedCatalogItems.size} ລາຍການ
                  {selectedAddableItems.length !== selectedCatalogItems.size
                    ? ` · ເພີ່ມໄດ້ ${selectedAddableItems.length}`
                    : ""}
                </p>
              </div>
              <ProductAllocationFields
                value={allocationDraft}
                onChange={setAllocationDraft}
                disabled={
                  !feedId || addProduct.isPending || addProducts.isPending
                }
              />
              <p className="text-muted-foreground text-xs">
                ໃຊ້ກັບສິນຄ້າທີ່ເພີ່ມໃໝ່ · ເພີ່ມທີ່ເລືອກ/ທັງໝົດໃຊ້ໂໝດນີ້ຕາມສິດ preorder ຈາກ POS
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant={
                    isPreAddDraftActive(preAddDraft) ? "default" : "outline"
                  }
                  disabled={!feedId}
                  className="gap-1"
                  onClick={() => {
                    if (!feedId) {
                      toast.error("ກະລຸນາເລືອກໂພສກ່ອນ");
                      return;
                    }
                    setPreAddDialogOpen(true);
                  }}
                >
                  <RiSettings3Line className="size-4" />
                  ຕັ້ງສ່ວນຫຼຸດ / option ກ່ອນເພີ່ມ
                  {isPreAddDraftActive(preAddDraft)
                    ? ` (${preAddDraftSelectedCount(preAddDraft)})`
                    : ""}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    !feedId ||
                    addProduct.isPending ||
                    addProducts.isPending ||
                    Boolean(allocationError) ||
                    catalogTotal === 0
                  }
                  onClick={() => void handleAddAllMatching()}
                >
                  ເພີ່ມທັງໝົດ
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    !feedId ||
                    addProduct.isPending ||
                    Boolean(allocationError) ||
                    addProducts.isPending ||
                    selectedAddableItems.length === 0
                  }
                  onClick={() => void handleBulkAdd()}
                >
                  ເພີ່ມທີ່ເລືອກ
                </Button>
                <Input
                  placeholder="ຄົ້ນຫາສິນຄ້າ / SKU / ສະແກນ barcode"
                  value={catalogSearchInput}
                  onChange={(e) => {
                    setCatalogSearchInput(e.target.value);
                    debouncedCatalogSearch(e.target.value);
                  }}
                  onKeyDown={(event) => void handleCatalogSearchKeyDown(event)}
                  className="w-full"
                />
              </div>
            </div>
            <QueryState
              result={catalogQuery}
              isFetching={
                catalogQuery.isFetching && !catalogQuery.isFetchingNextPage
              }
            >
              <div className="overflow-x-auto">
                <CatalogTable
                  data={catalogItems}
                  isLoading={catalogQuery.isLoading}
                  offset={0}
                  limit={CATALOG_PAGE_LIMIT}
                  total={catalogTotal}
                  products={catalogProducts}
                  inPostVariantIds={sessionProductVariantIds}
                  hasFeed={Boolean(feedId)}
                  isAdding={addProduct.isPending || addProducts.isPending}
                  selectedIds={selectedCatalogIds}
                  onSelectionChange={handleCatalogSelectionChange}
                  onPaginationChange={() => undefined}
                  hasNextPage={catalogQuery.hasNextPage}
                  isFetchingNextPage={catalogQuery.isFetchingNextPage}
                  onLoadMore={() => {
                    if (
                      catalogQuery.hasNextPage &&
                      !catalogQuery.isFetchingNextPage
                    ) {
                      void catalogQuery.fetchNextPage();
                    }
                  }}
                  onAdd={(item) => void handleAdd(item)}
                />
              </div>
            </QueryState>
          </section>

          <section className="min-w-0 overflow-hidden rounded-xl border bg-card">
            <div className="flex items-center justify-between border-b p-4">
              <div>
                <h2 className="font-medium">
                  ສິນຄ້າໃນໂພສ ({sessionProductTotal})
                </h2>
                <p className="text-muted-foreground text-xs">
                  ຈັດການແບບກຸ່ມສິນຄ້າ · ເລືອກແລ້ວ {selectedSessionProductIds.size}{" "}
                  ຕົວເລືອກ
                </p>
              </div>
            </div>
            {!feedId ? (
              <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                <p className="text-sm">ເພີ່ມສິນຄ້າເຂົ້າໃນໂພສ</p>
                <p className="text-xs">ເລືອກເພຈ ແລະ ໂພສກ່ອນ</p>
              </div>
            ) : (
              <QueryState result={sessionProductsQuery}>
                <div className="p-3">
                  <FeedProductsTable
                    products={sessionProductGroups}
                    isLoading={sessionProductsQuery.isFetching}
                    hasNextPage={sessionProductsQuery.hasNextPage}
                    isFetchingNextPage={sessionProductsQuery.isFetchingNextPage}
                    onLoadMore={() => void sessionProductsQuery.fetchNextPage()}
                    selectedIds={selectedSessionProductIds}
                    onSelectionChange={setSelectedSessionProductIds}
                    onBulkEditOptions={handleBulkEditOptions}
                    onBulkRemove={(ids) => void handleBulkRemove(ids)}
                    onToggleSale={(id, enabled) =>
                      patchSessionProduct.mutate({ id, saleEnabled: enabled })
                    }
                    onSaveCfCode={(id, cfCode) =>
                      patchSessionProduct.mutateAsync({ id, cfCode })
                    }
                    onSaveAllocation={(id, input) =>
                      patchSessionProduct.mutateAsync({ id, ...input })
                    }
                    onEditOptions={(id, title) => {
                      const row = sessionProducts.find((p) => p.id === id);
                      setOptionsTarget({
                        ids: [id],
                        title,
                        discountPercent: row?.discountPercent ?? null,
                        discountFixedLak: row?.discountFixedLak ?? null,
                      });
                    }}
                    onRemove={(id) => removeProduct.mutate(id)}
                    isSaving={patchSessionProduct.isPending}
                    isRemoving={
                      removeProduct.isPending || removeProductsBulk.isPending
                    }
                  />
                </div>
              </QueryState>
            )}
          </section>
        </div>
      </Main>

      {feedId ? (
        <PreAddSessionOptionsDialog
          feedId={feedId}
          variantId={selectedAddableItems[0]?.variantId ?? null}
          open={preAddDialogOpen}
          onOpenChange={setPreAddDialogOpen}
          initialDraft={preAddDraft}
          onSave={setPreAddDraft}
        />
      ) : null}

      {feedId ? (
        <SessionProductOptionsDialog
          feedId={feedId}
          sessionProductIds={optionsTarget?.ids ?? []}
          productTitle={optionsTarget?.title ?? ""}
          discountPercent={optionsTarget?.discountPercent ?? null}
          discountFixedLak={optionsTarget?.discountFixedLak ?? null}
          open={Boolean(optionsTarget)}
          onOpenChange={(next) => {
            if (!next) setOptionsTarget(null);
          }}
          onSaved={() => {
            setSelectedSessionProductIds(new Set());
            void queryClient.invalidateQueries({
              queryKey: feedsKeys.postContext(feedId),
            });
            void queryClient.invalidateQueries({
              queryKey: feedsKeys.detailSessionProductsInfinite(feedId),
            });
          }}
        />
      ) : null}
    </>
  );
}
